import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDate, parseSuburb } from "@/lib/format";
import { ArrowLeft, Printer, Loader2, Layers, Package, Boxes } from "lucide-react";

export default function BookingSlip() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: o, isLoading } = useQuery({ queryKey: ["order", id], queryFn: async () => (await api.get(`/orders/${id}`)).data });
  const { data: company = {} } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });

  if (isLoading || !o) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF6500]" /></div>;

  const cs = o.customer_snapshot || {};
  const comments = [o.special_instructions, o.delivery_notes].filter(Boolean).join(" · ");
  const state = parseSuburb(cs.delivery_address);
  const reference = o.po_number ? `ORDER ${o.po_number}` : `ORDER ${o.order_number}`;

  const Field = ({ label, value, big }) => (
    <div className="flex items-baseline justify-between gap-4 border-b border-slate-200 py-2">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <span className={`text-right font-bold text-[#0B192C] ${big ? "text-2xl" : "text-base"}`}>{value || "-"}</span>
    </div>
  );

  return (
    <div className="mx-auto max-w-4xl">
      {/* toolbar (hidden on print) */}
      <div className="no-print mb-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500"><ArrowLeft size={18} /> Back</button>
        <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/orders/${id}/booking/pdf`, "_blank")} data-testid="print-booking-slip-btn" className="flex items-center gap-2 rounded-xl bg-[#0B192C] px-5 py-2.5 text-sm font-bold text-white tap-shrink">
          <Printer size={18} /> Export Booking Slip
        </button>
      </div>

      {/* printable slip */}
      <div className="booking-print rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="booking-slip">
        {/* header */}
        <div className="flex items-start justify-between border-b-2 border-[#0B192C] pb-5">
          <img src="/gs-logo.png" alt="GS Pacific" className="h-16 w-auto rounded-md" />
          <div className="text-right">
            <h1 className="font-head text-3xl font-black tracking-tight text-[#0B192C]">BOOKING SLIP</h1>
            <p className="text-sm text-slate-500">{company.company_name || "GS Pacific Marketing Aust"}</p>
            {company.abn && <p className="text-xs text-slate-400">ABN {company.abn}</p>}
          </div>
        </div>

        {/* sender + receiver */}
        <div className="mt-6 grid grid-cols-2 gap-6">
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 inline-block rounded bg-[#0B192C] px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-white">Sender</div>
            <div className="font-head text-lg font-bold text-[#0B192C]">{company.company_name || "GS Pacific Marketing Aust"}</div>
            <div className="mt-1 whitespace-pre-line text-sm text-slate-600">{company.address || "Unit 1, 2 Costello Pl, Seven Hills NSW 2147"}</div>
            {company.phone && <div className="mt-1 text-sm text-slate-600">Ph: {company.phone}</div>}
            {company.email && <div className="text-sm text-slate-600">{company.email}</div>}
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="mb-2 inline-block rounded bg-[#FF6500] px-2 py-0.5 text-xs font-bold uppercase tracking-widest text-white">Receiver</div>
            <div className="font-head text-lg font-bold text-[#0B192C]">{cs.name}</div>
            <div className="mt-1 whitespace-pre-line text-sm text-slate-600">{cs.delivery_address}</div>
            {cs.contact_person && <div className="mt-1 text-sm text-slate-600">Attn: {cs.contact_person}</div>}
            {cs.phone && <div className="text-sm text-slate-600">Ph: {cs.phone}</div>}
          </div>
        </div>

        {/* consignment details */}
        <div className="mt-6 grid grid-cols-2 gap-x-10 gap-y-0">
          <div>
            <Field label="Date" value={fmtDate(o.delivery_date)} />
            <Field label="Job No" value={o.order_number} big />
            <Field label="Reference / Order No" value={reference} />
          </div>
          <div>
            <Field label="Suburb" value={state} big />
            <Field label="Delivery Time" value={o.delivery_time} />
            <Field label="Driver" value={o.driver_name || "Unassigned"} />
          </div>
        </div>

        {/* goods */}
        <div className="mt-6 grid grid-cols-3 gap-4">
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4"><Layers className="text-[#0B192C]" /><div><div className="text-2xl font-black text-[#0B192C]">{o.pallets}</div><div className="text-xs uppercase text-slate-500">Pallets</div></div></div>
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4"><Package className="text-[#0B192C]" /><div><div className="text-2xl font-black text-[#0B192C]">{o.cartons}</div><div className="text-xs uppercase text-slate-500">Cartons</div></div></div>
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 p-4"><Boxes className="text-[#0B192C]" /><div><div className="text-2xl font-black text-[#0B192C]">{o.total_items}</div><div className="text-xs uppercase text-slate-500">Total Items</div></div></div>
        </div>

        {/* comments */}
        <div className="mt-6">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Comments</div>
          <div className="mt-1 min-h-[60px] rounded-xl border border-slate-200 p-3 text-sm text-slate-700">{comments || "—"}</div>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">{company.company_name || "GS Pacific"} · Generated {fmtDate(new Date().toISOString())}</p>
      </div>
    </div>
  );
}
